import type { Editor } from "@tiptap/react";
import type { Asset } from "schemas/generated/asset";

import * as React from "react";

import { selectAsset } from "@agent-ui/features/chat/utils/contextItem";
import { Input } from "@replo/design-system/components/input/Input";
import { skipToken } from "@tanstack/react-query";
import { assetRouter } from "@trpc/asset";
import { AssetsGrid } from "@v2/features/assets/components/AssetsGrid";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { Search } from "lucide-react";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

export const InspirationMenu: React.FC<{
  editor: Editor | null;
  onClose?: () => void;
}> = ({ editor, onClose }) => {
  const projectId = useCurrentProjectId();
  const [searchInputValue, searchQuery, setSearchInputValue] =
    useDebouncedState("");

  // NOTE (Max, 2025-10-17): We default to "hero sections" if no search query is provided
  // as these sections generally look better than showing random inspiration assets.
  const adjustedSearchQuery = searchQuery === "" ? "hero" : searchQuery;

  const {
    data,
    isLoading: isAssetsLoading,
    fetchNextPage,
    hasNextPage,
  } = assetRouter.list.useInfiniteQuery(
    projectId
      ? {
          projectId,
          types: ["image"],
          limit: 16,
          search: adjustedSearchQuery,
          scope: "global",
        }
      : skipToken,
    {
      getNextPageParam: ({ nextCursor }) => nextCursor,
    },
  );

  const assets = React.useMemo(
    () => data?.pages?.flatMap((page) => page.assets) ?? [],
    [data],
  );

  const handleAssetSelect = (asset: Asset) => {
    selectAsset(asset, editor);
    onClose?.();
  };

  return (
    <div className="p-3 space-y-3 h-full pb-10">
      <Input
        placeholder="Search sections..."
        startEnhancer={<Search />}
        size="base"
        value={searchInputValue}
        onChange={(e) => setSearchInputValue(e.target.value)}
        autoFocus={true}
      />

      <div className="h-full flex flex-col">
        <AssetsGrid
          assets={assets}
          searchTerm={adjustedSearchQuery}
          isLoading={isAssetsLoading}
          hasNextPage={hasNextPage}
          fetchNextPage={() => void fetchNextPage()}
          emptyState={<div>No assets found</div>}
          onAssetSelected={handleAssetSelect}
        />
      </div>
    </div>
  );
};
